/* Created by Interactive 63 - https://interactive63.com */
/**
 * The inspector's text section: the content of a text element, its font family, size and weight,
 * and its alignment around the anchor point. These change the artwork itself; colour, position
 * and the rest animate through the ordinary property rows.
 */
import type { ElementId, PropertyKey } from '../../../core/model/types';
import { readText, type TextAnchor, type TextInfo } from '../../../core/text/text';
import { editTextContent, editTextStyle } from '../../store/text-actions';
import type { Store } from '../../store/store';
import { h } from '../../ui/dom';
import { NumberInput } from './inputs/number-input';

const FONT_SUGGESTIONS = [
  'system-ui, sans-serif',
  'Arial, Helvetica, sans-serif',
  'Verdana, Geneva, sans-serif',
  "'Trebuchet MS', sans-serif",
  'Impact, sans-serif',
  'Georgia, serif',
  "'Times New Roman', Times, serif",
  'ui-monospace, Consolas, monospace',
  "'Courier New', monospace",
  'cursive',
];

const WEIGHTS: { value: string; label: string }[] = [
  { value: '100', label: 'Thin' },
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semibold' },
  { value: '700', label: 'Bold' },
  { value: '900', label: 'Black' },
];

const ANCHORS: { value: TextAnchor; label: string; title: string }[] = [
  { value: 'start', label: 'Left', title: 'Text starts at the anchor point' },
  { value: 'middle', label: 'Centre', title: 'Text is centred on the anchor point' },
  { value: 'end', label: 'Right', title: 'Text ends at the anchor point' },
];

const MIN_FONT_SIZE = 0.1;
const MAX_FONT_SIZE = 10000;
const FONT_LIST_ID = 'text-font-suggestions';

export class TextSection {
  readonly el: HTMLElement;
  private readonly content: HTMLTextAreaElement;
  private readonly family: HTMLInputElement;
  private readonly size: NumberInput;
  private readonly weight: HTMLSelectElement;
  private readonly anchors: HTMLButtonElement[];
  private readonly note: HTMLElement;

  /** Builds the section for one text or tspan element. */
  constructor(
    private readonly store: Store,
    private readonly id: ElementId,
  ) {
    this.content = h('textarea', {
      class: 'text-content',
      rows: 2,
      spellcheck: 'false',
      'aria-label': 'Text content',
    });
    this.content.addEventListener('input', () => {
      editTextContent(this.store, this.id, this.content.value, `text:${this.id}`);
    });

    this.family = h('input', {
      class: 'text-family',
      list: FONT_LIST_ID,
      autocomplete: 'off',
      'aria-label': 'Font family',
      placeholder: 'Inherited',
    });
    this.family.addEventListener('change', () => {
      editTextStyle(this.store, this.id, { fontFamily: this.family.value });
    });
    this.family.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.family.blur();
    });
    const suggestions = h(
      'datalist',
      { id: FONT_LIST_ID },
      ...FONT_SUGGESTIONS.map((font) => h('option', { value: font })),
    );

    this.size = new NumberInput({
      label: 'Size',
      ariaLabel: 'Font size',
      step: 1,
      min: MIN_FONT_SIZE,
      max: MAX_FONT_SIZE,
      scrubKey: `scrub:font-size:${id}`,
      onChange: (fontSize, key) => {
        editTextStyle(this.store, this.id, { fontSize }, key);
      },
    });

    this.weight = h('select', { class: 'text-weight', 'aria-label': 'Font weight' });
    this.weight.addEventListener('change', () => {
      editTextStyle(this.store, this.id, { fontWeight: this.weight.value });
    });

    this.anchors = ANCHORS.map((anchor) => {
      const button = h(
        'button',
        { type: 'button', title: anchor.title, 'aria-pressed': 'false' },
        anchor.label,
      );
      button.addEventListener('click', () => {
        editTextStyle(this.store, this.id, { anchor: anchor.value });
      });
      return button;
    });

    this.note = h('p', { class: 'keyframe-notice' });

    this.el = h(
      'section',
      { class: 'inspector-section text-section', 'aria-label': 'Text' },
      h('label', { class: 'keyframe-field' }, 'Text', this.content),
      this.note,
      h('label', { class: 'keyframe-field' }, 'Font', this.family, suggestions),
      h('div', { class: 'text-size-row' }, this.size.el, this.weight),
      h(
        'div',
        { class: 'element-toggles', role: 'group', 'aria-label': 'Alignment' },
        ...this.anchors,
      ),
    );
    this.refresh();
  }

  /** Focuses the words field with its words selected, so typing replaces them. */
  focusContent(): void {
    if (this.content.hidden) return;
    this.content.focus();
    this.content.select();
  }

  /** Whether this element has keyframes for a property, which then owns its value. */
  private isKeyed(property: PropertyKey): boolean {
    return this.store
      .getState()
      .project.tracks.some(
        (t) => t.elementId === this.id && t.property === property && t.keyframes.length > 0,
      );
  }

  /** Updates the fields from the artwork, leaving alone any field that is being typed in. */
  refresh(): void {
    const info = readText(this.store.getState().project.svg.source, this.id);
    if (!info) return;
    const wordsKeyed = this.isKeyed('words');
    this.content.hidden = !info.editable || wordsKeyed;
    this.note.hidden = !this.content.hidden;
    this.note.textContent = info.editable
      ? 'The words are animated. Change them in the Words row below, or on their keyframes.'
      : 'This text is made of separate spans. Select a span in the Layers panel to edit its words.';
    if (document.activeElement !== this.content && this.content.value !== info.content) {
      this.content.value = info.content;
    }
    this.content.rows = Math.min(6, Math.max(2, info.content.split('\n').length));
    if (document.activeElement !== this.family) this.family.value = info.fontFamily;
    // An animated size is edited on its keyframes, through the Font size row.
    this.size.el.style.display = this.isKeyed('fontSize') ? 'none' : '';
    this.size.setValue(info.fontSize);
    this.syncWeights(info);
    for (const [index, button] of this.anchors.entries()) {
      button.setAttribute('aria-pressed', String(ANCHORS[index]?.value === info.anchor));
    }
  }

  /** Fills the weight list, keeping an unusual weight from the artwork selectable. */
  private syncWeights(info: TextInfo): void {
    const options = WEIGHTS.some((w) => w.value === info.fontWeight)
      ? WEIGHTS
      : [...WEIGHTS, { value: info.fontWeight, label: info.fontWeight }];
    if (this.weight.options.length !== options.length) {
      this.weight.replaceChildren(
        ...options.map((w) => h('option', { value: w.value }, `${w.label} (${w.value})`)),
      );
    }
    this.weight.value = info.fontWeight;
  }
}
